<template>
  <!-- H5 挂到 body，才能以 z-index 盖住 App 级 tabbar -->
  <teleport to="body" :disabled="!canTeleport">
  <view
    v-if="visible"
    class="lanhu-sheet-overlay"
    :class="{ 'is-center': placement === 'center' }"
    @click="onOverlayClick"
    @touchmove.stop.prevent
  >
    <view
      class="lanhu-sheet"
      :class="{ 'is-center': placement === 'center' }"
      @click.stop
      @touchmove.stop
    >
      <view
        class="lanhu-sheet-head"
        :class="{
          'head-minimal': !title && !description && !badge,
          'head-center': placement === 'center',
        }"
      >
        <view
          v-if="placement === 'center' && (title || showClose)"
          class="lanhu-sheet-head-center"
        >
          <text v-if="title" class="lanhu-sheet-title">{{ title }}</text>
          <view v-if="showClose" class="lanhu-sheet-close is-absolute" @click="handleClose">
            <image
              class="lanhu-close-icon"
              src="/static/image/lanhu/modal/close.webp"
              mode="aspectFit"
            />
          </view>
        </view>
        <view v-else-if="title || badge || showClose" class="lanhu-sheet-head-row">
          <view class="lanhu-sheet-head-side">
            <view v-if="badge" class="lanhu-sheet-badge" @click.stop="handleBadge">
              <text class="lanhu-sheet-badge-text">{{ badge }}</text>
            </view>
          </view>
          <text v-if="title" class="lanhu-sheet-title">{{ title }}</text>
          <view class="lanhu-sheet-head-side is-right">
            <view v-if="showClose" class="lanhu-sheet-close" @click="handleClose">
              <image
                class="lanhu-close-icon"
                src="/static/image/lanhu/modal/close.webp"
                mode="aspectFit"
              />
            </view>
          </view>
        </view>
        <text
          v-if="description"
          class="lanhu-sheet-desc"
          :class="{ 'is-center': placement === 'center' }"
        >{{ description }}</text>
      </view>

      <view class="lanhu-sheet-body" @touchmove.stop>
        <slot />
      </view>

      <view v-if="cancelText || confirmText" class="lanhu-sheet-footer" :class="{ single: !cancelText || !confirmText }">
        <view
          v-if="cancelText"
          class="lanhu-btn lanhu-btn-secondary"
          @click="handleCancel"
        >
          <text>{{ cancelText }}</text>
        </view>
        <view
          v-if="confirmText"
          class="lanhu-btn lanhu-btn-primary"
          :class="{ disabled: confirmDisabled }"
          @click="handleConfirm"
        >
          <text>{{ confirmText }}</text>
        </view>
      </view>
    </view>
  </view>
  </teleport>
</template>

<script setup lang="ts">
import { getCurrentInstance, nextTick, onUnmounted, toRef, watch } from 'vue';
import { useScrollLock } from '@/composables/useScrollLock';
import { useHideTabbarOnOverlay } from '@/composables/useHideTabbarOnOverlay';

const canTeleport = typeof document !== 'undefined';

const props = withDefaults(
  defineProps<{
    visible: boolean;
    title?: string;
    description?: string;
    badge?: string;
    showClose?: boolean;
    cancelText?: string;
    confirmText?: string;
    confirmDisabled?: boolean;
    closeOnOverlay?: boolean;
    /** bottom：底部抽屉；center：居中弹框 */
    placement?: 'bottom' | 'center';
  }>(),
  {
    title: '',
    description: '',
    badge: '',
    showClose: true,
    cancelText: '',
    confirmText: '',
    confirmDisabled: false,
    closeOnOverlay: true,
    placement: 'bottom',
  }
);

const sheetVisible = toRef(props, 'visible');
useScrollLock(sheetVisible);
useHideTabbarOnOverlay(sheetVisible);

const instance = getCurrentInstance();
let sheetResizeObserver: ResizeObserver | null = null;

const publishSheetHeight = (height: number) => {
  if (!Number.isFinite(height) || height < 80) return;
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(
    '--lanhu-sheet-match-height',
    `${Math.round(height)}px`
  );
};

const readSheetElement = (): HTMLElement | null => {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('.lanhu-sheet');
  return el instanceof HTMLElement ? el : null;
};

const measureSheet = () => {
  const el = readSheetElement();
  if (el?.offsetHeight) {
    publishSheetHeight(el.offsetHeight);
    return;
  }
  const proxy = instance?.proxy;
  if (!proxy || typeof uni === 'undefined' || !uni.createSelectorQuery) return;
  uni
    .createSelectorQuery()
    .in(proxy as any)
    .select('.lanhu-sheet')
    .boundingClientRect((rect: any) => {
      const height = Array.isArray(rect) ? rect[0]?.height : rect?.height;
      if (height) publishSheetHeight(height);
    })
    .exec();
};

const watchSheetSize = () => {
  const el = readSheetElement();
  sheetResizeObserver?.disconnect();
  sheetResizeObserver = null;
  if (!el || typeof ResizeObserver === 'undefined') return;
  sheetResizeObserver = new ResizeObserver(() => {
    publishSheetHeight(el.offsetHeight);
  });
  sheetResizeObserver.observe(el);
};

watch(
  () => props.visible,
  (open) => {
    if (!open) {
      sheetResizeObserver?.disconnect();
      sheetResizeObserver = null;
      return;
    }
    // 居中弹框不参与底部 sheet 高度同步
    if (props.placement === 'center') return;
    nextTick(() => {
      measureSheet();
      watchSheetSize();
    });
  }
);

onUnmounted(() => {
  sheetResizeObserver?.disconnect();
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'cancel'): void;
  (e: 'confirm'): void;
  (e: 'disabled-click'): void;
  (e: 'badge'): void;
  (e: 'update:visible', value: boolean): void;
}>();

const handleClose = () => {
  emit('update:visible', false);
  emit('close');
};

const handleCancel = () => {
  emit('cancel');
  emit('close');
  emit('update:visible', false);
};

const handleConfirm = () => {
  if (props.confirmDisabled) {
    emit('disabled-click');
    return;
  }
  emit('confirm');
};

const handleBadge = () => {
  emit('badge');
};

const onOverlayClick = () => {
  if (!props.closeOnOverlay) return;
  emit('close');
  emit('update:visible', false);
};
</script>

<style lang="scss" scoped>
@use '@/common/styles/sheet-modal.scss' as *;

.lanhu-sheet-overlay {
  @include lanhu-sheet-overlay;
  animation: lanhuSheetFadeIn 0.25s ease;

  &.is-center {
    align-items: center;
    padding: 48rpx;
    box-sizing: border-box;
  }
}

.lanhu-sheet {
  @include lanhu-sheet-panel;

  &.is-center {
    width: 100%;
    max-width: 560rpx;
    border-radius: 20rpx;
    padding-bottom: 40rpx;
    box-shadow: 0 8rpx 40rpx rgba(17, 24, 37, 0.18);
    animation: lanhuCenterFadeIn 0.22s ease;
  }
}

@keyframes lanhuCenterFadeIn {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.lanhu-sheet-head {
  position: relative;
  padding: 30rpx 34rpx 0;
  min-height: 100rpx;

  &.head-minimal {
    min-height: 90rpx;
    padding-bottom: 0;
  }

  &.head-center {
    min-height: 0;
    padding: 40rpx 36rpx 0;
  }
}

.lanhu-sheet-head-center {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44rpx;
  padding: 0 48rpx;
  box-sizing: border-box;
}

.lanhu-sheet-head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 70rpx;
  gap: 12rpx;
}

.lanhu-sheet-head-side {
  width: 168rpx;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  min-height: 70rpx;

  &.is-right {
    justify-content: flex-end;
  }
}

.lanhu-sheet-badge {
  max-width: 168rpx;
  width: fit-content;
  min-height: 48rpx;
  padding: 8rpx 16rpx;
  border-radius: 24rpx;
  background: $lanhu-badge-bg;
  border: 1rpx solid $lanhu-badge-border;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  .lanhu-sheet-badge-text {
    font-size: 24rpx;
    font-weight: 500;
    color: $lanhu-badge-text;
    line-height: 1.2;
    text-align: center;
    white-space: normal;
    word-break: break-word;
  }
}

.lanhu-sheet-title {
  flex: 1;
  min-width: 0;
  @include lanhu-sheet-title;
  padding: 0;
  margin: 0;
  line-height: 44rpx;
}

.lanhu-sheet-desc {
  display: block;
  margin: 16rpx 0 0;
  padding: 0;
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 24rpx;
  font-weight: 400;
  color: $lanhu-desc;
  line-height: 36rpx;
  text-align: left;

  &.is-center {
    margin-top: 20rpx;
    font-size: 26rpx;
    line-height: 1.55;
    text-align: left;
    color: #5b6470;
  }
}

.lanhu-sheet-close {
  position: static;
  flex-shrink: 0;
  @include lanhu-close-btn;

  &.is-absolute {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
  }
}

.lanhu-sheet-body {
  padding: 24rpx 34rpx 0;
}

.lanhu-sheet-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20rpx;
  padding: 40rpx 30rpx 0;
  margin: 0;
  box-sizing: border-box;

  &.single .lanhu-btn {
    flex: 1;
  }

  .lanhu-btn {
    flex: 1;
    min-width: 0;
  }
}

.lanhu-btn-primary {
  @include lanhu-primary-btn;

  text {
    color: #000000;
    font-size: 28rpx;
    font-weight: 500;
    line-height: 28rpx;
  }
}

.lanhu-btn-secondary {
  @include lanhu-secondary-btn;

  text {
    color: $lanhu-cancel-text;
    font-size: 28rpx;
    font-weight: 500;
    line-height: 28rpx;
  }
}
</style>
